import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import { IdParams, ProcumerentPlanParams } from 'types/api'
import {
  ICurrentProcurementPlan,
  IProcurementPlan,
  IProcurementPlanCorrelation,
  IProcurementPlans,
} from 'types/procurementPlans'
import { IPaginationQueryInfo } from 'types/pagination'

export const procurementPlansApi = createApi({
  reducerPath: 'procurementPlans',
  tagTypes: [
    'ProcurementPlans',
    'PlanCorrelations',
    'CurrentProcurementPlans',
    'ProcurementPlansSearch',
    'ProcurementPlan'
  ],
  baseQuery,
  endpoints: (builder) => ({
    procurementPlans: builder.query<IProcurementPlan[], IPaginationQueryInfo>({
      query: () => ({
        url: '/procurement-plans',
      }),
      providesTags: ['ProcurementPlans'],
    }),

    currentProcurementPlans: builder.query<ICurrentProcurementPlan, void>({
      query: () => ({
        url: '/procurement-plans/current',
      }),
      providesTags: ['CurrentProcurementPlans'],
    }),

    procurementPlansSearch: builder.query<IProcurementPlans, ProcumerentPlanParams>({
      query: ({ year, orgName, page, size }) => {
        const params = new URLSearchParams()

        if (size) {
          params.set('size', String(size))
        }
        if (page) {
          params.set('page', String(page))
        }

        if (orgName && orgName.length > 0) {
          params.set('orgName', orgName)
        }

        if (year && year.length > 0) {
          params.set('year', year)
        } else {
          params.set('year', new Date().getFullYear().toString())
        }

        return {
          url: `/procurement-plans/search`,
          method: 'get',
          params,
        }
      },
      providesTags: ['ProcurementPlansSearch'],
    }),

    procurementPlansRelations: builder.query<
      IProcurementPlanCorrelation[],
      IdParams
    >({
      query: ({ id }) => ({
        url: `/procurement-plans/${id}/correlations`,
      }),
      providesTags: ['PlanCorrelations'],
    }),

    procurementPlan: builder.query<IProcurementPlan, IdParams>({
      query: ({ id }) => `/procurement-plans/${id}`,
      providesTags: ['ProcurementPlan'],
    }),

    deleteProcurementPlan: builder.mutation<void, IdParams>({
      query: ({ id }) => ({
        url: `/procurement-plans/${id}`,
        method: 'delete',
      }),
      invalidatesTags: ['PlanCorrelations', 'ProcurementPlans'],
    }),

    createProcurementPlanDraft: builder.mutation<
      IProcurementPlan,
      Partial<IProcurementPlan>
    >({
      query: (body) => ({
        url: '/procurement-plans/draft',
        method: 'post',
        body,
      }),
      invalidatesTags: ['PlanCorrelations', 'ProcurementPlans'],
    }),

    updateProcurementPlan: builder.mutation<
      IProcurementPlan,
      { id: string; procurementPlan: IProcurementPlan }
    >({
      query: ({ id, procurementPlan }) => ({
        url: `/procurement-plans/${id}`,
        body: procurementPlan,
        method: 'put',
      }),
      invalidatesTags: ['PlanCorrelations', 'ProcurementPlans'],
    }),

    publishProcurementPlan: builder.mutation<
      IProcurementPlan,
      { body: Partial<IProcurementPlan>; archive: boolean }
    >({
      query: ({ archive, body }) => ({
        url: '/procurement-plans/publish',
        method: 'post',
        body,
        params: {
          archive,
        },
      }),
      invalidatesTags: ['PlanCorrelations', 'ProcurementPlans'],
    }),
  }),
})

export const {
  useProcurementPlanQuery,
  useProcurementPlansQuery,
  useProcurementPlansSearchQuery,
  useUpdateProcurementPlanMutation,
  useDeleteProcurementPlanMutation,
  useCreateProcurementPlanDraftMutation,
  useProcurementPlansRelationsQuery,
  usePublishProcurementPlanMutation,
  useCurrentProcurementPlansQuery,
} = procurementPlansApi
